import { useEffect, useState } from "react";
import { Button } from "~/components/ui/button";
import { MarkdownEditor } from "~/components/ui/markdown-editor";
import { Skeleton } from "~/components/ui/skeleton";
import useToast from "~/components/ui/toast";
import { usePrompt } from "~/hooks/usePrompt";
import { SettingsPage } from "~/pages/manifold-nextgen/components/settings/SettingsPage";
import { PERSONALIZATION_MAX_LENGTH } from "~/services/preferences";
import { usePersonalization } from "./usePersonalization";

const UNSAVED_CHANGES_MESSAGE = "You have unsaved changes. Leave without saving?";

const PLACEHOLDER = `Anything you'd tell a new colleague on their first day. For example:

- I'm a cardiologist studying heart failure readmissions.
- Report counts alongside percentages, and use metric units.
- Prefer Python to R if possible.
- Skip the preamble — lead with the answer.`;

/**
 * The profile's Personalization tab: the user's own standing instructions to
 * every agent, in their own words.
 *
 * Saved explicitly rather than on blur — the text goes into every conversation
 * they have afterwards, so committing it is their decision.
 */
export const ProfilePersonalizationTab = () => {
  const { showSuccess, showError } = useToast();
  const { text, isSaving, save } = usePersonalization();

  const [draft, setDraft] = useState<string | null>(null);

  // Seed the draft from the loaded preference, once. Later revalidations must not
  // overwrite what the user is in the middle of typing.
  useEffect(() => {
    setDraft((current) => (current === null && text !== null ? text : current));
  }, [text]);

  // Compared trimmed, because the service stores the text stripped: an edit that
  // only adds trailing whitespace would otherwise stay dirty forever after saving.
  const isDirty = draft !== null && text !== null && draft.trim() !== text.trim();

  usePrompt(isDirty ? UNSAVED_CHANGES_MESSAGE : false, { beforeUnload: true });

  const handleSave = async () => {
    if (draft === null) return;
    try {
      await save(draft);
      showSuccess("Personalization saved");
    } catch {
      showError("Unable to save your personalization");
    }
  };

  return (
    <SettingsPage
      title="Personalization"
      description="Tell the agents who you are and how you want them to work. This applies to every conversation you start."
    >
      <section className="flex flex-col gap-2">
        {/* No card around the editor — it brings its own frame, and its textarea
            a second one. The header row matches a SettingsGroup's. */}
        <div className="flex min-h-6 items-center justify-between gap-4 px-0.5">
          <h2 className="truncate text-sm font-medium text-foreground">Your instructions</h2>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDraft(text)}
              disabled={!isDirty || isSaving}
            >
              Discard
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!isDirty || isSaving}
              loading={isSaving}
            >
              Save
            </Button>
          </div>
        </div>
        {draft === null ? (
          <Skeleton className="h-72 w-full" />
        ) : (
          <MarkdownEditor
            value={draft}
            onChange={setDraft}
            readOnly={isSaving}
            maxLength={PERSONALIZATION_MAX_LENGTH}
            placeholder={PLACEHOLDER}
            aria-label="Your instructions"
          />
        )}
      </section>
    </SettingsPage>
  );
};
